import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@kp/react-sdk';

import { useQuery } from '@tanstack/react-query';

import { getUser, getUserSettings } from '../api/user';

interface Settings {
  language?: string;
  roles?: string[];
}

interface SettingsProviderProps {
  children: React.ReactNode;
}

type SettingsContextValue = {
  settings: Settings;
};

export const SettingsContext = createContext<SettingsContextValue>({
  settings: {},
});

export const useSettings = () => {
  const context = useContext(SettingsContext);
  /* v8 ignore next 3 */
  if (!context) {
    throw new Error(
      'useSettingsContext must be used within a SettingsContext Provider',
    );
  }
  return context;
};

export const SettingsProvider: React.FC<SettingsProviderProps> = ({
  children,
}) => {
  const [settings, setSettings] = useState<Settings>({});
  const { isAuthenticated, user } = useAuth();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (settings?.language) {
      i18n.changeLanguage(settings.language);
    }
  }, [settings, i18n]);

  const { refetch, data, error } = useQuery({
    queryKey: ['getUserSettings'],
    queryFn: getUserSettings,
    enabled: isAuthenticated,
  });

  const { data: userData } = useQuery({
    queryKey: ['getUser'],
    queryFn: () => getUser(user?.info?.sub ?? ''),
    enabled: isAuthenticated,
  });

  useEffect(() => {
    if (data) {
      setSettings(() => ({
        language: data.data.language,
        roles: userData?.data?.roles.map((role) => role.name),
      }));
    }

    if (error) {
      setSettings({});
    }
  }, [data, userData, error]);

  useEffect(() => {
    refetch();
  }, [isAuthenticated, refetch]);

  const value = useMemo(
    () => ({
      settings,
    }),
    [settings],
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
